import {Button, Skeleton, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';
import BrithDateIcon from '@icons/BrithDateIcon';
import CityIcon from '@icons/CityIcon';
import GenderIcon from '@icons/GenderIcon';
import NameIcon from '@icons/NameIcon';
import NationalCodeIcon from '@icons/NationalCodeIcon';
import PhoneNumberIcon from '@icons/PhoneNumberIcon';
import socket, {IResponse} from '@sockets';
import HeaderWithIcons from '@components/app/HeaderWithIcons';
import Card from '../card/Card';

export interface IUser {
	firstname: string,
	lastname: string,
	nationalCode: string,
	username: string,
	phone: string,
	state: string,
	city: string,
	gender: 'male' | 'female',
	birthDate: Date,
	createdAt: Date,
}

export default function Profile() {
	const {t} = useTranslation();
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState<IUser>();
	const navigate = useNavigate();
	
	useEffect(() => {
		if (localStorage.getItem('isLogin') !== 'true') {
			const lastLogin = Number(localStorage.getItem('lastLogin') ?? '0');
			if (new Date().getTime() - lastLogin > 180000) {
				localStorage.setItem('lastLogin', new Date().getTime().toString());
				socket.emit('profile:login', null, (data: IResponse<string>) => {
					if (data.success) {
						window.location.href = data.result;
					}
				});
			} else {
				localStorage.removeItem('lastLogin');
				navigate('/');
			}
		} else {
			socket.emit('profile:info', null, (data: IResponse<IUser>) => {
				if (data.success) {
					setLoading(false);
					setUser(data.result);
				} else if (/^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(data.error)) {
					const lastLogin = Number(localStorage.getItem('lastLogin') ?? '0');
					if (new Date().getTime() - lastLogin > 180000) {
						localStorage.setItem('lastLogin', new Date().getTime().toString());
						localStorage.removeItem('isLogin');
						window.location.href = data.error;
					} else {
						localStorage.removeItem('lastLogin');
						navigate('/');
					}
				} else {
					// TODO show error
				}
			});
		}
	}, []);
	return (
		<div>
			<HeaderWithIcons title={t('profile')} backUrl="/"/>
			<Card className="mx-4" title={t('profile_information')}
			      action={<Button component={Link} state={{user}} to="/profile/edit">{t('edit')}</Button>}>
				<div className="flex flex-col p-2 space-y-2">
					<div className="flex flex-row h-10 items-center space-x-2 rtl:space-x-reverse">
						<NameIcon/>
						{
							loading ?
								<Skeleton className="w-24"/>
								:
								<Typography
									sx={{color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200'}}>{`${user?.firstname ?? ''} ${user?.lastname ?? ''}`}</Typography>
						}
					</div>
					<div className="flex flex-row h-10 items-center space-x-2 rtl:space-x-reverse">
						<NationalCodeIcon/>
						<Typography sx={{color: 'grey.500'}}>{t('national_code')}:</Typography>
						{
							loading ?
								<Skeleton className="w-24"/>
								:
								<Typography
									sx={{color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200'}}>{`${user?.nationalCode ?? ''}`}</Typography>
						}
					</div>
					<div className="flex flex-row h-10 items-center space-x-2 rtl:space-x-reverse">
						<PhoneNumberIcon/>
						<Typography sx={{color: 'grey.500'}}>{t('phone')}:</Typography>
						{
							loading ?
								<Skeleton className="w-24"/>
								:
								<Typography
									sx={{color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200'}}>{`${user?.phone ?? ''}`}</Typography>
						}
					</div>
					<div className="flex flex-row h-10 items-center space-x-2 rtl:space-x-reverse">
						<BrithDateIcon/>
						<Typography sx={{color: 'grey.500'}}>{t('birth_date')}:</Typography>
						{
							loading ?
								<Skeleton className="w-24"/>
								:
								<Typography sx={{color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200'}}>
									{`${user?.birthDate ? new Date(user?.birthDate).toLocaleDateString('fa', {
										day: 'numeric',
										month: 'long',
										year: 'numeric'
									}) : ''}`}
								</Typography>
						}
					</div>
					
					<div className="flex flex-row h-10 items-center space-x-2 rtl:space-x-reverse">
						<GenderIcon/>
						<Typography sx={{color: 'grey.500'}}>{t('gender')}:</Typography>
						{
							loading ?
								<Skeleton className="w-24"/>
								:
								<Typography sx={{color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200'}}>
									{`${user?.gender ? (user?.gender === 'male' ? t('male') : t('female')) : ''}`}
								</Typography>
						}
					</div>
					<div className="flex flex-row h-10 items-center space-x-2 rtl:space-x-reverse">
						<CityIcon/>
						<Typography sx={{color: 'grey.500'}}>{t('city')}:</Typography>
						{
							loading ?
								<Skeleton className="w-24"/>
								:
								<Typography sx={{color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200'}}>
									{user?.city ?? ''}
								</Typography>
						}
					</div>
				</div>
			</Card>
		</div>
	);
}
